// src/pages/CreateTeamPage.tsx
import React, { Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage, FieldArray, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext';

// --- Reusable Form Components ---
const FormInput = ({ name, type = 'text', label, ...props }: any) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-300">{label}</label>
        <Field
            id={name}
            name={name}
            type={type}
            className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            {...props}
        />
        <ErrorMessage name={name} component="p" className="text-red-400 text-xs italic mt-1" />
    </div>
);

const FormSelect = ({ name, label, children }: any) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-300">{label}</label>
        <Field
            as="select"
            id={name}
            name={name}
            className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
            {children}
        </Field>
        <ErrorMessage name={name} component="p" className="text-red-400 text-xs italic mt-1" />
    </div>
);

const FormTextArea = ({ name, label, ...props }: any) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-300">{label}</label>
        <Field
            as="textarea"
            id={name}
            name={name}
            className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            {...props}
        />
        <ErrorMessage name={name} component="p" className="text-red-400 text-xs italic mt-1" />
    </div>
);


// --- Main Page Component ---
const CreateTeamPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showNotification } = useNotification();

    const initialValues = {
        name: '',
        discipline: 'Football',
        logoUrl: '',
        clubInfo: '',
        players: [], // Start with an empty array
        achievements: [], // Start with an empty array
    };

    const validationSchema = Yup.object({
        name: Yup.string().required(t('validation.required')),
        discipline: Yup.string().required(t('validation.required')),
        // KEY FIX: The 'players' field is now optional and doesn't have a .min(1) rule.
        players: Yup.array().of(
            Yup.object().shape({
                name: Yup.string().required('Nazwa zawodnika jest wymagana'),
                isCaptain: Yup.boolean(),
            })
        ),
        achievements: Yup.array().of(Yup.string()),
    });

    const handleSubmit = async (values: any, { setSubmitting }: any) => {
        const finalValues = {
            ...values,
            achievements: values.achievements.filter((ach: string) => ach && ach.trim() !== '')
        };
        try {
            await api.post('/Teams', finalValues);
            showNotification('Drużyna została pomyślnie utworzona!', 'success');
            navigate('/teams');
        } catch (error) {
            showNotification('Nie udało się utworzyć drużyny.', 'error');
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto bg-gray-800 p-8 rounded-lg shadow-lg">
            <h1 className="text-3xl font-bold text-white text-center mb-8">{t('createTeamPage.title')}</h1>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ values, isSubmitting }) => (
                    <Form className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormInput name="name" label={t('createTeamPage.teamNameLabel')} />
                            <FormSelect name="discipline" label={t('createTeamPage.disciplineLabel')}>
                                <option value="Football">{t('sportTypes.Football')}</option>
                                <option value="Basketball">{t('sportTypes.Basketball')}</option>
                                <option value="Volleyball">{t('sportTypes.Volleyball')}</option>
                            </FormSelect>
                        </div>

                        <FormInput name="logoUrl" label={t('createTeamPage.logoUrlLabel')} />
                        <FormTextArea name="clubInfo" label={t('createTeamPage.clubInfoLabel')} rows={4} />

                        <hr className="border-gray-700 my-4" />

                        <FieldArray name="players">
                            {({ push, remove }) => (
                                <div>
                                    <h3 className="text-xl font-semibold text-white mb-2">{t('createTeamPage.playersSectionTitle')}</h3>
                                    {values.players.map((player, index) => (
                                        <div key={index} className="flex items-center gap-4 mb-2 p-2 bg-gray-700/50 rounded-md">
                                            <Field name={`players.${index}.name`} placeholder="Imię i nazwisko" className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 flex-grow" />
                                            <label className="flex items-center text-gray-300 whitespace-nowrap">
                                                <Field type="checkbox" name={`players.${index}.isCaptain`} className="h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500 border-gray-500 bg-gray-800 mr-2" />
                                                {t('createTeamPage.captainLabel')}
                                            </label>
                                            <button type="button" onClick={() => remove(index)} className="text-red-500 hover:text-red-400 font-bold text-2xl">&times;</button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => push({ name: '', isCaptain: false })} className="mt-2 text-sm text-indigo-400 hover:text-indigo-300">{t('createTeamPage.addPlayerButton')}</button>
                                </div>
                            )}
                        </FieldArray>

                        <hr className="border-gray-700 my-4" />

                        <FieldArray name="achievements">
                            {({ push, remove }) => (
                                <div>
                                    <h3 className="text-xl font-semibold text-white mb-2">{t('createTeamPage.achievementsSectionTitle')}</h3>
                                    {values.achievements.map((achievement, index) => (
                                        <div key={index} className="flex items-center gap-4 mb-2">
                                            <Field name={`achievements.${index}`} placeholder="{t('createTeamPage.achievementPlaceholder')}" className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 flex-grow" />
                                            <button type="button" onClick={() => remove(index)} className="text-red-500 hover:text-red-400 font-bold text-2xl">&times;</button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => push('')} className="mt-2 text-sm text-indigo-400 hover:text-indigo-300">{t('createTeamPage.addAchievementButton')}</button>
                                </div>
                            )}
                        </FieldArray>

                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                            >
                                {isSubmitting ? t('common.loading') : "Utwórz Drużynę"}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

const CreateTeamPage = () => (
    <Suspense fallback={<div className="text-center p-8 text-white">Ładowanie...</div>}>
        <CreateTeamPageContent />
    </Suspense>
);

export default CreateTeamPage;
